"use client";

import { useEffect } from "react";
import { toast } from "sonner";

import { createStaleServerActionRuntime } from "@/lib/stale-server-action-guard";

export function StaleServerActionGuard() {
  useEffect(() => {
    const runtime = createStaleServerActionRuntime({
      onToast(kind, context) {
        if (kind === "refreshing") {
          toast.info("App updated. Refreshing page…", {
            id: "stale-server-action-refreshing",
            description:
              "We detected an outdated page session and are reloading once to restore the latest version.",
            duration: 2500,
          });
          return;
        }

        if (kind === "manual-refresh") {
          toast.error("Please refresh to continue", {
            id: "stale-server-action-manual-refresh",
            description:
              "This tab is still using outdated app state. Refresh the page to load the latest version.",
            duration: 60000,
            action: {
              label: "Refresh now",
              onClick: () => window.location.reload(),
            },
          });
          return;
        }

        if (context.reloadStrategy === "on-visible") {
          toast.info("New version available", {
            id: `stale-server-action-version-drift-${context.serverBuildId ?? "unknown"}`,
            description:
              "This tab is using an older app version. Refresh now, or switch away and back once to let the app reload automatically.",
            duration: Number.POSITIVE_INFINITY,
            action: {
              label: "Refresh now",
              onClick: () => window.location.reload(),
            },
          });
          return;
        }

        toast.info("New version available", {
          id: `stale-server-action-version-drift-${context.serverBuildId ?? "unknown"}`,
          description:
            "A newer app version is available. Refresh when convenient to avoid stale state.",
          duration: 8000,
          action: {
            label: "Refresh",
            onClick: () => window.location.reload(),
          },
        });
      },
    });

    runtime.start();
    return () => runtime.stop();
  }, []);

  return null;
}
